import type { Attachment, CreateAttachmentInput, Page, PresignedUpload } from '@servicetim/shared';
import { useMutation } from '@tanstack/react-query';
import { useConfig } from '../lib/config';
import { ApiError, portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const attachmentsPath = (ticketId: string) => portalPath(`/tickets/${ticketId}/attachments`);
const attachmentPath = (attachmentId: string) => portalPath(`/attachments/${attachmentId}`);

/** `GET /v1/tickets/{id}/attachments` (keyset pages). */
export function useTicketAttachments(ticketId: string | undefined, pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<Attachment>({
    queryKey: [...keys.ticket(ticketId ?? '').attachments, { pageSize }],
    fetchPage: (cursor, signal) =>
      request<Page<Attachment>>('GET', attachmentsPath(ticketId ?? ''), {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    enabled: ticketId !== undefined,
  });
}

/** `POST /v1/tickets/{id}/attachments` → pending row + S3 presigned POST. */
export function usePresignAttachment(ticketId: string) {
  const { request } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateAttachmentInput) =>
      request<PresignedUpload>('POST', attachmentsPath(ticketId), { body: input }),
  });
}

/** `POST /v1/attachments/{aid}/complete` after the S3 upload succeeded. */
export function useCompleteAttachment(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      request<Attachment>('POST', `${attachmentPath(attachmentId)}/complete`, { body: {} }),
    onSuccess: () => invalidate(keys.ticket(ticketId).attachments, keys.ticket(ticketId).history),
  });
}

export function useDeleteAttachment(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (attachmentId: string) => request<void>('DELETE', attachmentPath(attachmentId)),
    onSuccess: () => invalidate(keys.ticket(ticketId).attachments),
  });
}

/**
 * Presign → upload to S3 (multipart form POST with the returned fields) → complete. Returns the
 * ready attachment. Throws `ApiError` (status 0) when the S3 POST fails.
 */
export function useUploadAttachment(ticketId: string) {
  const presign = usePresignAttachment(ticketId);
  const complete = useCompleteAttachment(ticketId);
  return useMutation({
    mutationFn: async ({ file, commentId }: { file: File; commentId?: string | null }) => {
      const presigned = await presign.mutateAsync({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        commentId: commentId ?? null,
      });
      const form = new FormData();
      for (const [key, value] of Object.entries(presigned.upload.fields)) form.append(key, value);
      form.append('file', file);
      const response = await fetch(presigned.upload.url, { method: 'POST', body: form });
      if (!response.ok) {
        throw new ApiError({
          status: 0,
          url: presigned.upload.url,
          message: `Upload to storage failed (${response.status})`,
        });
      }
      return complete.mutateAsync(presigned.attachmentId);
    },
  });
}

/**
 * `GET /v1/attachments/{aid}/download` answers a 302 to a presigned URL; open it in a new tab
 * or set `window.location`. Returns the absolute API URL for an anchor `href`.
 */
export function useAttachmentDownloadUrl(): (attachmentId: string) => string {
  const { apiBaseUrl } = useConfig();
  return (attachmentId) =>
    `${apiBaseUrl.replace(/\/+$/, '')}${attachmentPath(attachmentId)}/download`;
}
